<template>
  <div
    id="sim-results-question-review"
    data-test="sim-results-question-review"
  >
    <breadcrumbs
      :breadcrumbs="breadcrumbs"
      version="v2"
    ></breadcrumbs>
    <answered-flashcard
      :answer-ids="answerIds"
      :category="currentFlashcardCategory"
      :correct="correctlyAnswered"
      :flashcard="currentFlashcard"
      :raw-answer-data="reviewRawAnswerData"
    ></answered-flashcard>
    <answered-footer
      button-text="Done"
      :disable-next="disableNext"
      :disable-previous="disablePrevious"
      :hide-primary="showNext"
      reserve-primary-space
      :show-next="showNext"
      :show-previous="showPrevious"
      submit-style
      @next-flashcard="goToNextFlashcard"
      @previous-flashcard="goToPreviousFlashcard"
      @tag="goToNextFlashcard"
    ></answered-footer>
  </div>
</template>

<script>
import AnsweredFlashcard from '@/components/flashcards-v2/AnsweredFlashcard';
import AnsweredFooter from '@components/flashcards-v2/AnsweredFooter';
import Breadcrumbs from '@components/singletons/breadcrumbs';
import BlueprintMixin from '@/mixins/blueprint';
import GlobalMixin from '@/mixins/global';
import { rateLimitingEnabled } from '@/utils';

// An ordered response left in its initial order is stored as an empty payload.
// The simulation stores an empty payload when the submitted order matches its order.
function startingOrderedAnswerIds(flashcard) {
  const answers = flashcard?.answers || [];
  const pseudoRandom = (id) => {
    const seed = flashcard.id || 0;
    const value = Math.sin(seed + id) * 10000;
    return value - Math.floor(value);
  };
  const correctIds = [...answers]
    .sort((a, b) => a.correctOrder - b.correctOrder)
    .map(answer => answer.id);
  let ordered = [...answers].sort((a, b) => a.sortOrder - b.sortOrder);
  if (flashcard?.randomizedSortOrder) {
    ordered = [...answers].sort((a, b) => pseudoRandom(a.id) - pseudoRandom(b.id));
  }
  const orderedIds = ordered.map(answer => answer.id);
  const matchesCorrect = orderedIds.length === correctIds.length
    && orderedIds.every((id, index) => id === correctIds[index]);
  return matchesCorrect ? [...orderedIds].reverse() : orderedIds;
}

export default {
  name: 'SimResultsQuestionReview',
  components: { AnsweredFlashcard, AnsweredFooter, Breadcrumbs },
  mixins: [BlueprintMixin, GlobalMixin],
  data() {
    return {
      answerIds: [],
      rawAnswerData: null,
    };
  },
  computed: {
    breadcrumbs() {
      if (this.isSectionResults) {
        const rootCategory = this.$store.getters['categories/categories'][this.categoryId] || {};
        const section = this.$store.getters['categories/categories'][this.sectionId] || {};
        return [
          {
            name: rootCategory.name,
            route: {
              name: 'sims#performance',
              params: { simId: this.categoryId },
              query: this.currentQuery,
            },
          },
          {
            name: section.name,
            route: {
              name: 'sims#sectionResults',
              params: {
                categoryId: this.categoryId,
                sectionId: this.sectionId,
              },
              query: this.currentQuery,
            },
          },
          {
            name: 'Review',
          },
        ];
      }

      const category = this.$store.getters['categories/categories'][this.categoryId] || {};
      return [
        {
          name: category.name,
          route: {
            name: 'sims#performance',
            params: { simId: this.categoryId },
            query: this.currentQuery,
          },
        },
        {
          name: this.blueprintGrouping.name,
          route: {
            name: 'sims#results',
            params: {
              categoryId: this.categoryId,
              blueprintId: this.blueprintId,
            },
            query: this.currentQuery,
          },
        },
        {
          name: 'Review',
        },
      ];
    },
    categoryId() {
      return this.$route.params.categoryId;
    },
    correctlyAnswered() {
      return !!this.currentFlashcardState?.correct;
    },
    currentFlashcard() {
      return this.$store.getters['flashcards/flashcards'][this.flashcardId] || {};
    },
    currentFlashcardCategory() {
      const categoryId = this.isSectionResults ? this.sectionId : this.categoryId;
      return this.$store.getters['categories/categories'][categoryId] || null;
    },
    currentFlashcardState() {
      return this.orderedStates.find(state => String(state.flashcardId) === String(this.flashcardId)) || null;
    },
    reviewRawAnswerData() {
      if (this.currentFlashcard?.type !== 'Ordered Response') return this.rawAnswerData;
      if (this.rawAnswerData?.orderedAnswerIds?.length) return this.rawAnswerData;
      if (!this.rawAnswerData || !this.currentFlashcard.answers?.length) return this.rawAnswerData;
      return {
        ...this.rawAnswerData,
        orderedAnswerIds: startingOrderedAnswerIds(this.currentFlashcard),
      };
    },
    currentIndex() {
      return this.orderedStates.findIndex(state => String(state.flashcardId) === String(this.flashcardId));
    },
    disableNext() {
      return !this.showNext;
    },
    disablePrevious() {
      return !this.showPrevious;
    },
    doneRoute() {
      if (this.isSectionResults) {
        return {
          name: 'sims#sectionResults',
          params: {
            categoryId: this.categoryId,
            sectionId: this.sectionId,
          },
          query: this.currentQuery,
        };
      }
      return {
        name: 'sims#results',
        params: {
          categoryId: this.categoryId,
          blueprintId: this.blueprintId,
        },
        query: this.currentQuery,
      };
    },
    flashcardId() {
      return this.$route.params.flashcardId;
    },
    isSectionResults() {
      return this.$route.name === 'sims#sectionResults#question';
    },
    nextFlashcardId() {
      if (this.currentIndex < 0 || this.currentIndex >= this.orderedStates.length - 1) return null;
      return this.orderedStates[this.currentIndex + 1].flashcardId;
    },
    orderedStates() {
      if (this.isSectionResults) {
        const states = this.$store.getters['flashcardStates/flashcardStateList']
          .filter(state => state.categoryId == this.sectionId);
        return [...states].sort(this.flashcardStateComparator);
      }
      return this.getBlueprintFlashcardStates(this.categoryId);
    },
    previousFlashcardId() {
      if (this.currentIndex <= 0) return null;
      return this.orderedStates[this.currentIndex - 1].flashcardId;
    },
    sectionId() {
      return this.$route.params.sectionId;
    },
    showNext() {
      return this.currentIndex >= 0 && this.currentIndex < this.orderedStates.length - 1;
    },
    showPrevious() {
      return this.currentIndex > 0;
    },
  },
  watch: {
    currentFlashcardState: {
      immediate: true,
      handler(state) {
        this.answerIds = state?.answerIds || [];
        this.rawAnswerData = state?.rawAnswerData || null;
      },
    },
  },
  created() {
    this.$store.dispatch('flashcardStates/fetchStates', ['flashcards']);
    this.fetchSimFlashcards();
  },
  methods: {
    fetchSimFlashcards() {
      const categoryId = this.isSectionResults ? this.sectionId : this.categoryId;
      if (!categoryId) return;
      this.$store.dispatch('flashcards/fetchFlashcards', {
        categoryId,
        rateLimited: rateLimitingEnabled({
          store: this.$store,
          apptimize: this.$apptimize,
          analytics: this.$analytics,
        }),
      }).then(() => {
        this.resetMemoFlashcardsBySection();
      });
    },
    goToNextFlashcard() {
      if (this.nextFlashcardId) {
        this.$router.push(this.routeForFlashcard(this.nextFlashcardId));
        return;
      }
      this.$router.push(this.doneRoute);
    },
    goToPreviousFlashcard() {
      if (!this.previousFlashcardId) return;
      this.$router.push(this.routeForFlashcard(this.previousFlashcardId));
    },
    routeForFlashcard(flashcardId) {
      const params = {
        categoryId: this.categoryId,
        flashcardId,
      };
      if (this.isSectionResults) {
        params.sectionId = this.sectionId;
      } else {
        params.blueprintId = this.blueprintId;
      }
      return {
        name: this.isSectionResults ? 'sims#sectionResults#question' : 'sims#results#question',
        params,
        query: this.currentQuery,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
  #sim-results-question-review {
    @include flashcard-view-container-v2;
  }
</style>
